import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  ACTION_ORDER,
  ACTIONS,
  actionLabels,
  assigneeName,
  LAUNCH_TASKS,
  clientLabels,
  clientOnlyTransition,
  clientView,
  initialClientOnly,
  matrixMessage,
  ownershipColumns,
  ownershipMessage,
  ownershipRows,
  ownershipRule,
  PEOPLE,
  people,
  permissionMatrix,
  pythonSet,
  roleFact,
  STATUS_REQUEST_ORDER,
  statusDecision,
  statusRequests,
  stepOutcomeText,
  taskPage,
  verdict,
  visibleTaskIds,
  type Action,
  type Client,
  type ClientOnlyAction,
  type Person,
  type StatusRequest,
} from './permission-models';

import './relationship-labs.css';
import './permission-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
  className = '',
  current = null,
  verdicts = false,
  muted,
}: {
  label: string;
  columns: string[];
  rows: string[][];
  template?: string;
  className?: string;
  /** The selected cell as [row, column]. */
  current?: [number, number] | null;
  /** Colour every cell after the first as a permission verdict. */
  verdicts?: boolean;
  /** Rows to mute, such as tasks the viewer cannot see. */
  muted?: (row: string[]) => boolean;
}) {
  return (
    <div className="rel-table-scroll">
      <div className={`rel-table cb-record-grid ${className}`} role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.map((row, index) => (
          <div
            role="row"
            className="rel-row"
            key={index}
            data-muted={muted?.(row) ? 'true' : undefined}
            style={{ gridTemplateColumns: template }}
          >
            {row.map((cell, column) => (
              <span
                role="cell"
                key={column}
                aria-current={current?.[0] === index && current[1] === column ? 'true' : undefined}
                data-verdict={verdicts && column > 0 ? cell : undefined}
                data-null={cell === 'NULL' || undefined}
              >
                {cell}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="cb-check">
      <input type="checkbox" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}
const SIMULATION = "A simulation of the server's permission checks on the lesson's data.";
const personOptions = (labels: 'role' | 'name') =>
  PEOPLE.map((person) => ({
    value: person,
    label: labels === 'role' ? people[person].label : people[person].name,
  }));

export function PermMatrixLab() {
  const [person, setPerson] = useState<Person>('ravi');
  const [action, setAction] = useState<Action>('delete_task');
  const [checkRoles, setCheckRoles] = useState(true);
  const rows = permissionMatrix(checkRoles);
  const selected = verdict(person, action, checkRoles);
  return (
    <Lab
      title="What may each person do in Apollo?"
      eyebrow="Permissions model · roles"
      className="rel-lab perm-lab"
    >
      <Predict>
        Maya manages Apollo, Ravi is a member and Jo is in no project. Predict which cells say 403
        and which say 404 once the server reads roles, then switch off Check roles to see what it
        allowed before.
      </Predict>
      <div className="cb-controls">
        <label>
          Person
          <ModelSelect
            aria-label="Person"
            value={person}
            onValueChange={(value) => setPerson(value as Person)}
            options={personOptions('role')}
          />
        </label>
        <label>
          Action
          <ModelSelect
            aria-label="Action"
            value={action}
            onValueChange={(value) => setAction(value as Action)}
            options={ACTION_ORDER.map((key) => ({ value: key, label: actionLabels[key] }))}
          />
        </label>
      </div>
      <div className="cb-controls">
        <Toggle
          label="Check roles"
          checked={checkRoles}
          onChange={() => setCheckRoles((current) => !current)}
        />
        <Reset
          onClick={() => {
            setPerson('ravi');
            setAction('delete_task');
            setCheckRoles(true);
          }}
        />
      </div>
      <Table
        label="Permissions in Apollo"
        columns={['Action', ...PEOPLE.map((key) => people[key].label)]}
        template="minmax(0, 1.3fr) repeat(3, minmax(0, 1fr))"
        className="perm-matrix"
        verdicts
        current={[ACTION_ORDER.indexOf(action), PEOPLE.indexOf(person) + 1]}
        rows={rows.map((row) => [row.label, ...row.cells])}
      />
      <Facts
        values={[
          ['Selected cell', `${people[person].label} · ${actionLabels[action]} → ${selected}`],
          ['Role in Apollo', roleFact(person)],
          [`ACTIONS["${action}"]`, pythonSet(ACTIONS[action])],
        ]}
      />
      <Result tone={checkRoles ? 'success' : 'warning'}>
        {matrixMessage(person, action, checkRoles)}
      </Result>
      <p className="rel-caption">
        {SIMULATION} Allowed means the server performs the action; 403 and 404 are refusals. The
        table is deny by default: an action or a role it does not list is never allowed.
      </p>
    </Lab>
  );
}

export function PermOwnershipLab() {
  const [viewer, setViewer] = useState<Person>('ravi');
  const [owners, setOwners] = useState(true);
  const name = people[viewer].name;
  return (
    <Lab
      title="Whose tasks does the task page show?"
      eyebrow="Permissions model · ownership"
      className="rel-lab perm-lab"
    >
      <Predict>
        Tasks 1, 4 and 8 are Maya&apos;s personal tasks, 11 is Jo&apos;s, and 9 and 10 belong to
        Apollo. Predict what Ravi&apos;s task page shows without owners, and what it shows once
        every personal task has one.
      </Predict>
      <div className="cb-controls">
        <label>
          Viewer
          <ModelSelect
            aria-label="Viewer"
            value={viewer}
            onValueChange={(value) => setViewer(value as Person)}
            options={personOptions('name')}
          />
        </label>
      </div>
      <div className="cb-controls">
        <Toggle
          label="Personal tasks have owners"
          checked={owners}
          onChange={() => setOwners((current) => !current)}
        />
        <Reset
          onClick={() => {
            setViewer('ravi');
            setOwners(true);
          }}
        />
      </div>
      <Table
        label="tasks table"
        columns={ownershipColumns(viewer, owners)}
        template={`32px minmax(0, 1.6fr) minmax(0, 0.9fr)${owners ? ' minmax(0, 0.9fr)' : ''} minmax(0, 1fr)`}
        className="perm-tasks"
        muted={(row) => row.at(-1) === 'hidden'}
        rows={ownershipRows(viewer, owners)}
      />
      <Panel label={`${name}'s task page`}>
        <pre aria-label="Task page">{taskPage(viewer, owners).join('\n')}</pre>
      </Panel>
      <Facts
        values={[
          ['Rule', ownershipRule(owners)],
          ['Tasks on the page', visibleTaskIds(viewer, owners).join(', ')],
        ]}
      />
      <Result tone={owners ? 'success' : 'warning'}>{ownershipMessage(viewer, owners)}</Result>
      <p className="rel-caption">
        {SIMULATION} Migration 4 adds owner_id to tasks and gives every existing personal task to
        Maya; Jo&apos;s task 11 gets his ID when he creates it. Project tasks keep owner_id NULL and
        follow membership instead.
      </p>
    </Lab>
  );
}

export function PermClientOnlyLab() {
  const [state, setState] = useState(initialClientOnly);
  const act = (action: ClientOnlyAction) =>
    setState((current) => clientOnlyTransition(current, action));
  const view = clientView(state.client);
  const attempt = state.attempt;
  const tasks = attempt ? attempt.tasksAfter : LAUNCH_TASKS;
  return (
    <Lab
      title="Does hiding the button protect the task?"
      eyebrow="Permissions model · server checks"
      className="rel-lab perm-lab"
    >
      <Predict>
        Ravi&apos;s page shows Delete only to managers, so he never sees it. Predict what happens
        when he sends the same request with curl, first without the server&apos;s role check and
        then with it.
      </Predict>
      <div className="cb-controls">
        <label>
          Client
          <ModelSelect
            aria-label="Client"
            value={state.client}
            onValueChange={(value) => act({ kind: 'set-client', client: value as Client })}
            options={(Object.keys(clientLabels) as Client[]).map((key) => ({
              value: key,
              label: clientLabels[key],
            }))}
          />
        </label>
      </div>
      <div className="cb-controls">
        <Toggle
          label="Server checks the role"
          checked={state.serverChecks}
          onChange={() => act({ kind: 'set-server-checks', on: !state.serverChecks })}
        />
        <Button size="sm" onClick={() => act({ kind: 'delete' })}>
          Ravi deletes task 10
        </Button>
        <Reset onClick={() => act({ kind: 'reset' })} />
      </div>
      <div className="cb-two">
        <Panel label={view.label}>
          <pre aria-label="Client view">{view.lines.join('\n')}</pre>
        </Panel>
        <Panel label="Server and response">
          <pre aria-label="Server and response">
            {attempt
              ? [...attempt.server, '', ...attempt.response].join('\n')
              : '(nothing sent yet)'}
          </pre>
        </Panel>
      </div>
      <Table
        label="Apollo's tasks"
        columns={['id', 'title', 'assignee']}
        template="32px minmax(0, 1.6fr) minmax(0, 1fr)"
        rows={tasks.map((task) => [String(task.id), task.title, assigneeName(task)])}
      />
      <Facts
        values={[
          ['Task 10', tasks.some((task) => task.id === 10) ? 'still in Apollo' : 'deleted'],
          [
            'Last attempt',
            attempt
              ? `${clientLabels[attempt.client]} · ${attempt.status ?? 'nothing sent'}`
              : '(none yet)',
          ],
        ]}
      />
      <Result
        tone={attempt?.status === 204 ? 'warning' : attempt?.status === 403 ? 'success' : 'neutral'}
      >
        {state.message}
      </Result>
      <p className="rel-caption">
        {SIMULATION} Every attempt starts from the lesson data, where Apollo has tasks 9 and 10.
        curl sends any method to any path with whatever cookie it is given.
      </p>
    </Lab>
  );
}

export function PermStatusLab() {
  const [request, setRequest] = useState<StatusRequest>('no-cookie-read');
  const decision = statusDecision(request);
  return (
    <Lab
      title="Which status does each refusal get?"
      eyebrow="Permissions model · status codes"
      className="rel-lab perm-lab"
    >
      <Predict>
        Each request passes three checks in order. Predict where each one stops, and why Jo should
        get 404 for Apollo rather than 403.
      </Predict>
      <div className="cb-controls">
        <label>
          Request
          <ModelSelect
            aria-label="Request"
            value={request}
            onValueChange={(value) => setRequest(value as StatusRequest)}
            options={STATUS_REQUEST_ORDER.map((key) => ({
              value: key,
              label: statusRequests[key].label,
            }))}
          />
        </label>
        <Reset onClick={() => setRequest('no-cookie-read')} />
      </div>
      <ol className="perm-path" aria-label="Decision path">
        {decision.steps.map((step, index) => (
          <li
            key={step.label}
            data-outcome={step.outcome}
            aria-current={decision.stoppedAt === index ? 'step' : undefined}
          >
            <span>{index + 1}</span>
            <strong>{step.label}</strong>
            <small>{stepOutcomeText(step, decision.status)}</small>
            {step.detail ? <p>{step.detail}</p> : null}
          </li>
        ))}
      </ol>
      <div className="cb-two">
        <Panel label="Request sent">
          <pre aria-label="Request sent">{decision.request.join('\n')}</pre>
        </Panel>
        <Panel label="Response">
          <pre aria-label="Response">{decision.response.join('\n')}</pre>
        </Panel>
      </div>
      <Facts
        values={[
          [
            'Stopped at',
            decision.stoppedAt === null
              ? 'Passed every check'
              : (decision.steps[decision.stoppedAt]?.label ?? ''),
          ],
          ['Status', decision.statusText],
        ]}
      />
      <Result tone={decision.stoppedAt === null ? 'success' : 'neutral'}>{decision.reveals}</Result>
      <p className="rel-caption">
        {SIMULATION} 401 answers who is asking, 404 answers what this person may see, and 403
        answers what this person may do with something they can see.
      </p>
    </Lab>
  );
}
